import { useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Plus, Search } from "lucide-react";

/**
 * Type-to-search picker that can also create a new record inline.
 * options: [{ value, label, sublabel }]
 */
export const SearchableSelect = ({
  value,
  options,
  onChange,
  onCreate,
  placeholder = "Search…",
  createLabel = (text) => `Add “${text}”`,
  emptyLabel = "No matches yet",
  testId,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const boxRef = useRef(null);

  const selected = options.find((o) => o.value === value);
  const matches = useMemo(() => {
    const text = query.trim().toLowerCase();
    if (!text) return options;
    return options.filter((o) => `${o.label} ${o.sublabel || ""}`.toLowerCase().includes(text));
  }, [options, query]);

  const canCreate = Boolean(onCreate) && query.trim().length >= 2 && !matches.some((o) => o.label.toLowerCase() === query.trim().toLowerCase());

  const pick = (option) => {
    onChange(option.value);
    setOpen(false);
    setQuery("");
  };

  const create = async () => {
    setCreating(true);
    try {
      const created = await onCreate(query.trim());
      if (created?.value) onChange(created.value);
      setOpen(false);
      setQuery("");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="relative mt-1.5" ref={boxRef} data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 text-left text-sm transition-colors hover:border-emerald-300"
        data-testid={`${testId}-trigger`}
      >
        <span className={selected ? "truncate text-slate-900" : "truncate text-slate-400"}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown className="ml-2 h-4 w-4 shrink-0 text-slate-400" />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
          <div className="flex items-center gap-2 border-b border-slate-100 px-3">
            <Search className="h-3.5 w-3.5 text-slate-400" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Type a name…"
              className="h-10 w-full bg-transparent text-sm outline-none placeholder:text-slate-400"
              data-testid={`${testId}-search`}
            />
          </div>
          <ul className="max-h-56 overflow-y-auto py-1" data-testid={`${testId}-options`}>
            {matches.map((o) => (
              <li key={o.value}>
                <button
                  type="button"
                  onClick={() => pick(o)}
                  className="flex w-full items-start justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-emerald-50"
                  data-testid={`${testId}-option-${o.value}`}
                >
                  <span>
                    <span className="block font-medium text-slate-800">{o.label}</span>
                    {o.sublabel && <span className="block text-xs text-slate-500">{o.sublabel}</span>}
                  </span>
                  {o.value === value && <Check className="mt-0.5 h-4 w-4 text-emerald-700" />}
                </button>
              </li>
            ))}
            {!matches.length && !canCreate && (
              <li className="px-3 py-3 text-xs text-slate-500" data-testid={`${testId}-empty`}>{emptyLabel}</li>
            )}
            {canCreate && (
              <li className="border-t border-slate-100">
                <button
                  type="button"
                  disabled={creating}
                  onClick={create}
                  className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm font-semibold text-emerald-800 hover:bg-emerald-50 disabled:opacity-60"
                  data-testid={`${testId}-create`}
                >
                  <Plus className="h-4 w-4" /> {creating ? "Adding…" : createLabel(query.trim())}
                </button>
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};
